"use client"

import type React from "react"
import { useEffect, useState, useCallback } from "react"
import { Container, Row, Col, Button, Alert, Card, CardBody } from "reactstrap"
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

// Dashboard Components
import StatsCards from "./StatsCards"
import RealTimeStats from "./RealTimeStats"
import MiniCharts from "./MiniCharts"
import DetailedCharts from "./DetailedCharts"
import SummaryCards from "./SummaryCards"
import AnalyticsDashboard from "./analytics-dashboard"
import StaffDashboard from "../StaffDashboard/StaffDashboard"

// Services and Types
import DashboardService from "../../../Service/dashboardService"
import type { DashboardStats, DashboardChartsData, RealTimeStats as RealTimeStatsType } from "./types/dashboard"
import AuthService from "../../../Service/AuthService"

// Exhibitor Reports Component
import ExhibitorReportsContainer from "../exhibitorReports/exhibitor-reports-container"

interface UserRoles {
  is_registrar: boolean
  is_staff: boolean
  is_verifier: boolean
  is_admin: boolean
  is_superuser: boolean
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [chartsData, setChartsData] = useState<DashboardChartsData | null>(null)
  const [realTimeStats, setRealTimeStats] = useState<RealTimeStatsType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [userRoles, setUserRoles] = useState<UserRoles>({
    is_registrar: false,
    is_staff: false,
    is_verifier: false,
    is_admin: false,
    is_superuser: false,
  })

  // Effect to extract user roles from token on component mount
  useEffect(() => {
    const decodedToken = AuthService.getDecodedToken()
    if (decodedToken) {
      setUserRoles({
        is_registrar: decodedToken.is_registrar || false,
        is_staff: decodedToken.is_staff || false,
        is_verifier: decodedToken.is_verifier || false,
        is_admin: decodedToken.is_admin || false,
        is_superuser: decodedToken.is_superuser || false,
      })
    }
  }, [])

  // Determine what content to show based on user roles
  const shouldShowExhibitorReports = userRoles.is_registrar
  const shouldShowStaffDashboard =
    userRoles.is_staff && !userRoles.is_verifier && !userRoles.is_admin && !userRoles.is_superuser
  const shouldShowMainDashboard = userRoles.is_verifier || userRoles.is_admin || userRoles.is_superuser

  const fetchDashboardData = useCallback(async () => {
    if (!shouldShowMainDashboard) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      // Fetch data from different apps including exhibitor badge stats
      const [exhibitorData, badgeData, entryData, badgeChartsData, entryChartsData, realTimeData, exhibitorBadgeStats] =
        await Promise.all([
          DashboardService.getExhibitorStats(),
          DashboardService.getBadgeStats(),
          DashboardService.getEntryStats(),
          DashboardService.getBadgeChartsData(),
          DashboardService.getEntryChartsData(),
          DashboardService.getRealTimeStats(),
          DashboardService.getBadgeStatsByExhibitorType(),
        ])

      // Transform old API structure to new structure
      const transformedTotalBadges = badgeData.totalBadges
        ? {
            total: badgeData.totalBadges.total || 0,
            free: badgeData.totalBadges.free || 0,
            extra: badgeData.totalBadges.extra || 0,
          }
        : { total: 0, free: 0, extra: 0 }

      // Calculate extraBadgeAmount if not provided by API
      const extraBadgeAmount = badgeData.extraBadgeAmount || {
        totalAmount: 0,
        badgeFee: 0,
      }

      // Combine all stats with transformation
      const combinedStats: DashboardStats = {
        totalExhibitors: exhibitorData.totalExhibitors,
        totalBadges: transformedTotalBadges,
        badgePayments: badgeData.badgePayments,
        extraBadgeAmount: extraBadgeAmount,
        ticketStats: entryData.ticketStats,
        vehicleStats: entryData.vehicleStats,
        freeBadges: badgeData.freeBadges || { count: 0 },
        reprintStats: badgeData.reprintStats || { totalAmount: 0, count: 0 },
        exceedsStats: badgeData.exceedsStats || { totalAmount: 0, count: 0 },
        hostBadges: badgeData.hostBadges || { totalAmount: 0, count: 0 },
        // Add exhibitor badge stats
        exhibitorBadgeStats: exhibitorBadgeStats,
      }

      // Combine charts data
      const combinedChartsData: DashboardChartsData = {
        ticketTrends: entryChartsData.ticketTrends,
        vehicleTrends: entryChartsData.vehicleTrends,
        badgeVerifications: badgeChartsData,
      }

      setStats(combinedStats)
      setChartsData(combinedChartsData)
      setRealTimeStats(realTimeData)
      setLastUpdated(new Date())
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err)
      setError(err instanceof Error ? err.message : "Failed to load dashboard data. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [shouldShowMainDashboard])

  const handleRefresh = () => {
    fetchDashboardData()
  }

  useEffect(() => {
    fetchDashboardData()
    // Set up auto-refresh every 5 minutes, only if should show main dashboard
    let interval: NodeJS.Timeout | undefined
    if (shouldShowMainDashboard) {
      interval = setInterval(fetchDashboardData, 5 * 60 * 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [fetchDashboardData, shouldShowMainDashboard])

  // Loading state for main dashboard
  if (loading && shouldShowMainDashboard) {
    return (
      <div className="db-page">
        <Breadcrumbs mainTitle="Dashboard" parent="Home" />
        <Container fluid>
          <Row>
            <Col>
              <div className="text-center py-5">
                <div className="spinner-border" style={{ color: "var(--db-indigo)" }} role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading dashboard data…</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  // Error state for main dashboard
  if (error && shouldShowMainDashboard && !stats) {
    return (
      <>
        <Breadcrumbs mainTitle="Dashboard" parent="Home" />
        <Container fluid>
          <Row>
            <Col>
              <Alert color="danger" className="d-flex align-items-center">
                <i className="fa fa-exclamation-triangle me-2"></i>
                <div>
                  <strong>Dashboard Error:</strong> {error}
                </div>
              </Alert>
              <div className="text-center">
                <Button color="primary" onClick={handleRefresh} disabled={loading}>
                  <i className={`fa fa-refresh me-2 ${loading ? "fa-spin" : ""}`}></i>
                  Retry Loading Dashboard
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </>
    )
  }

  // Show Exhibitor Reports for registrar users
  if (shouldShowExhibitorReports) {
    return (
      <>
        <Breadcrumbs mainTitle="Dashboard" parent="Home" />
        <Container fluid>
          <ExhibitorReportsContainer />
        </Container>
      </>
    )
  }

  // Show Staff Dashboard for staff users who are not verifier, admin, or superuser
  if (shouldShowStaffDashboard) {
    return <StaffDashboard />
  }

  // Show main dashboard for verifier, admin, or superuser
  if (shouldShowMainDashboard) {
    return (
      <div className="db-page">
        <Breadcrumbs mainTitle="Dashboard" parent="Home" />
        <Container fluid className="pb-4">
          {/* Header with Actions */}
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  {lastUpdated && (
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                      <i className="fa fa-clock-o me-1"></i>
                      Last updated: {lastUpdated.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button color="outline-primary" size="sm" onClick={handleRefresh} disabled={loading}>
                    <i className={`fa fa-refresh me-2 ${loading ? "fa-spin" : ""}`}></i>
                    Refresh
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Error Alert */}
          {error && stats && (
            <Row className="mb-3">
              <Col>
                <Alert color="warning" className="d-flex align-items-center">
                  <i className="fa fa-exclamation-circle me-2"></i>
                  <div>
                    <strong>Warning:</strong> {error}
                  </div>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Real-time Stats Banner */}
          <div className="db-section">
            <RealTimeStats realTimeStats={realTimeStats} loading={loading} />
          </div>

          {/* Main Stats Cards */}
          <div className="db-section">
            <div className="db-section-head">
              <span className="db-section-bar" style={{ background: "var(--db-indigo)" }}></span>
              <div>
                <p className="db-section-eyebrow">Overview</p>
                <h3 className="db-section-title">Key Metrics</h3>
              </div>
            </div>
            <StatsCards stats={stats} loading={loading} />
          </div>

          <div className="db-section">
            <AnalyticsDashboard />
          </div>

          {/* Mini Charts */}
          <div className="db-section">
            <div className="db-section-head">
              <span className="db-section-bar" style={{ background: "var(--db-emerald)" }}></span>
              <div>
                <p className="db-section-eyebrow">This Week</p>
                <h3 className="db-section-title">Quick Trends Overview</h3>
              </div>
            </div>
            <MiniCharts chartsData={chartsData} loading={loading} />
          </div>

          {/* Detailed Charts */}
          <div className="db-section">
            <div className="db-section-head">
              <span className="db-section-bar" style={{ background: "var(--db-cyan)" }}></span>
              <div>
                <p className="db-section-eyebrow">Trends Over Time</p>
                <h3 className="db-section-title">Detailed Analytics</h3>
              </div>
            </div>
            <DetailedCharts chartsData={chartsData} loading={loading} />
          </div>

          {/* Summary Cards */}
          <div className="db-section mb-0">
            <div className="db-section-head">
              <span className="db-section-bar" style={{ background: "var(--db-amber)" }}></span>
              <div>
                <p className="db-section-eyebrow">Breakdown</p>
                <h3 className="db-section-title">Summary Reports</h3>
              </div>
            </div>
            <SummaryCards stats={stats} realTimeStats={realTimeStats} loading={loading} />
          </div>
        </Container>
      </div>
    )
  }

  // Fallback for users with no recognized permissions
  return (
    <>
      <Breadcrumbs mainTitle="Dashboard" parent="Home" />
      <Container fluid>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-lg border-0">
              <CardBody className="text-center py-5">
                <div className="mb-4">
                  <i className="fa fa-lock fa-5x text-warning mb-3"></i>
                </div>
                <h2 className="text-warning mb-3">Access Restricted</h2>
                <p className="text-muted mb-4 lead">
                  You don't have the necessary permissions to access this dashboard. Please contact your administrator
                  for assistance.
                </p>
                <Button color="primary" onClick={() => window.location.reload()}>
                  <i className="fa fa-refresh me-2"></i>
                  Refresh Page
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Dashboard
